import { useEffect, useState } from 'react';
import type { Tag } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { TagPicker } from '@/components/TagPicker';

/**
 * Tags an eine Auswahl von Leads hängen.
 *
 * `tags.setForContact` ersetzt die ganze Menge eines Kontakts — für einen
 * einzelnen Kontakt im Bearbeiten-Formular ist das richtig, für eine Auswahl
 * mit unterschiedlichem Vorbestand wäre es das nicht: wer schon „Rabatte“
 * trägt, soll das beim Hinzufügen von „Jobs“ nicht verlieren. Deshalb wird je
 * Kontakt mit seinem eigenen Bestand vereinigt, statt ihn zu überschreiben.
 */
interface LeadFuerTagging {
  id: string;
  displayName: string;
  tags: Tag[];
}

interface Props {
  leads: LeadFuerTagging[];
  onClose: () => void;
  onDone: () => void;
}

export function TagSelectionDialog({ leads, onClose, onDone }: Props) {
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const anwenden = async () => {
    if (gewaehlt.length === 0) return;
    setBusy(true);
    setFehler(null);
    try {
      await Promise.all(
        leads.map((lead) => {
          const vereinigt = new Set([...lead.tags.map((t) => t.id), ...gewaehlt]);
          return crm.tags.setForContact(lead.id, [...vereinigt]);
        }),
      );
      onDone();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Taggen fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-md space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold">
          {leads.length} {leads.length === 1 ? 'Lead' : 'Leads'} taggen
        </h2>
        <p className="text-sm text-muted-foreground">
          Diese Tags kommen zu den vorhandenen dazu — nichts wird entfernt.
        </p>

        <TagPicker selected={gewaehlt} onChange={setGewaehlt} disabled={busy} />

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={() => void anwenden()} disabled={busy || gewaehlt.length === 0}>
            {busy ? 'Speichere …' : 'Tags übernehmen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
